/**
 * AuthService — JWT auth for the fulfillment panel.
 * Auth endpoints are unauthenticated, so we use plain axios instead of createAxiosClient.
 */
import axios from 'axios'
import { authApiUrl } from '@/constants/urls'
import {
  asOptionalString,
  asOptionalStringArray,
  asOptionalRecord,
  asRecord,
  type JsonRecord,
} from '@/utils/data'

export interface PhoneLoginPayload {
  mobile: string
  /** base64 of the plain password */
  password: string
}

export interface LoginResponse {
  accessToken: string
  userRoles?: string[]
  userEmail?: string
  userName?: string
  refreshToken?: string
  sessionId?: string
  userInfo?: Record<string, unknown>
  permissions?: string[]
  user?: { id?: string; email?: string; name?: string; roles?: string[] }
}

const decodeJwtPayload = (token: string): JsonRecord | undefined => {
  try {
    const payload = token.split('.')[1]
    if (!payload) return undefined

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    return asOptionalRecord(JSON.parse(window.atob(padded)))
  } catch {
    return undefined
  }
}

const rolesFromTenantMapping = (value: unknown): string[] | undefined => {
  const mapping = asRecord(value)
  const c2b = asRecord(mapping?.C2B)
  return asOptionalStringArray(c2b.default) ?? asOptionalStringArray(mapping.default)
}

const unwrapLoginData = (value: unknown): JsonRecord => {
  const data = asRecord(value)
  return asOptionalRecord(data.detail) ?? asOptionalRecord(data.data) ?? data
}

const normalizeLoginResponse = (value: unknown, fallbackEmail?: string): LoginResponse => {
  const root = asRecord(value)
  const data = unwrapLoginData(value)
  const user = asOptionalRecord(data.user) ?? asRecord(data.userInfo)
  const accessToken =
    asOptionalString(value) ??
    asOptionalString(root.detail) ??
    asOptionalString(root.data) ??
    asOptionalString(data.accessToken) ??
    asOptionalString(data.token) ??
    asOptionalString(data.jwt) ??
    asOptionalString(data.authToken) ??
    asOptionalString(data.jwtToken)

  if (!accessToken) {
    throw new Error('Login response did not include a JWT token')
  }

  const jwtPayload = decodeJwtPayload(accessToken)
  const userRoles =
    asOptionalStringArray(data.userRoles) ??
    asOptionalStringArray(data.appRoles) ??
    asOptionalStringArray(data.roles) ??
    asOptionalStringArray(user.roles) ??
    rolesFromTenantMapping(user.tenantRolesMapping)
  // Roles come from the response only: the backend reads them from the
  // database, so roles decoded from the JWT can be stale.

  return {
    accessToken,
    userRoles,
    userEmail:
      asOptionalString(data.userEmail) ??
      asOptionalString(data.email) ??
      asOptionalString(user.email) ??
      asOptionalString(jwtPayload?.email) ??
      fallbackEmail,
    userName:
      asOptionalString(data.userName) ??
      asOptionalString(data.name) ??
      asOptionalString(user.name) ??
      asOptionalString(jwtPayload?.name),
    refreshToken: asOptionalString(data.refreshToken),
    sessionId: asOptionalString(data.sessionId),
    userInfo: user,
    permissions:
      asOptionalStringArray(data.permissions) ??
      asOptionalStringArray(user.permissions) ??
      asOptionalStringArray(jwtPayload?.permissions),
    user: user as LoginResponse['user'],
  }
}

export const AuthService = {
  phoneLogin: (payload: PhoneLoginPayload): Promise<LoginResponse> =>
    axios
      .post(`${authApiUrl()}/auth/phone/login`, {
        mobile: payload.mobile,
        password: payload.password,
      }, {
        headers: { 'Content-Type': 'application/json' },
      })
      .then((r) => normalizeLoginResponse(r.data)),
}
